import type { ProjectNode } from '@/shared/types/projectData';

const DOCUMENT_MARKER_TAGS = new Set([
  'document',
  'doc',
  'docs',
  'meeting',
  'meeting-note',
  'meeting-notes',
  'meeting-report',
  'report',
  'evidence',
  'decision',
  'decision-log',
  'reference',
  'reference-note',
  'memo',
  'internal-memo',
  'brief',
  'summary',
]);

const DOCUMENT_MARKER_CATEGORIES = new Set([
  'document',
  'doc',
  'docs',
  'meeting',
  'meeting-note',
  'meeting-report',
  'report',
  'evidence',
  'decision',
  'decision-log',
  'reference',
  'reference-note',
  'memo',
  'internal-memo',
  'brief',
  'summary',
]);

const MEETING_MARKERS = new Set([
  'meeting',
  'meeting-note',
  'meeting-notes',
  'meeting-report',
  'minutes',
]);

export const normalizeDocumentMarker = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase().replace(/_/g, '-');

const markerFromCustomFields = (customFields?: Partial<ProjectNode>['customFields']): string => {
  if (!customFields || typeof customFields !== 'object') {
    return '';
  }

  return normalizeDocumentMarker(
    customFields['category'] ?? customFields['content_category'] ?? customFields['contentCategory']
  );
};

export const hasDocumentRecordMarkers = (node: Partial<ProjectNode>): boolean => {
  const hasTagMarker = Array.isArray(node.tags) && node.tags.some((tag) =>
    DOCUMENT_MARKER_TAGS.has(normalizeDocumentMarker(tag))
  );
  if (hasTagMarker) {
    return true;
  }

  if (DOCUMENT_MARKER_CATEGORIES.has(normalizeDocumentMarker(node.category))) {
    return true;
  }

  const customFieldCategory = markerFromCustomFields(node.customFields);
  return customFieldCategory ? DOCUMENT_MARKER_CATEGORIES.has(customFieldCategory) : false;
};

export const isDocumentDisplayRecord = (node: Partial<ProjectNode>): boolean => {
  if (node.type === 'document') {
    return true;
  }

  if (node.type !== 'task' && node.type !== 'milestone') {
    return false;
  }

  return hasDocumentRecordMarkers(node);
};

export const isMeetingRecord = (node: Partial<ProjectNode>): boolean => {
  if (!isDocumentDisplayRecord(node)) {
    return false;
  }

  if (node.workstream === 'meeting') {
    return true;
  }

  const hasMeetingTag = Array.isArray(node.tags) && node.tags.some((tag) =>
    MEETING_MARKERS.has(normalizeDocumentMarker(tag))
  );
  if (hasMeetingTag) {
    return true;
  }

  if (MEETING_MARKERS.has(normalizeDocumentMarker(node.category))) {
    return true;
  }

  const customFieldCategory = markerFromCustomFields(node.customFields);
  return customFieldCategory ? MEETING_MARKERS.has(customFieldCategory) : false;
};

export const isInternalOnlyDocumentRecord = (node: Partial<ProjectNode>): boolean =>
  node.visibilityTier === 'internal' &&
  Array.isArray(node.visibleToAccountIds) &&
  node.visibleToAccountIds.length > 0;
